import { ArrowRight, PlayCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackCTA } from "@/lib/analytics";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-36 pb-24 lg:pt-44 lg:pb-36 overflow-hidden"
    >
      <div className="absolute inset-0 radial-glow pointer-events-none" />
      <div
        className="absolute inset-0 opacity-[0.15] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage:
            "radial-gradient(ellipse at center top, black 30%, transparent 70%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Left */}
          <div className="lg:col-span-7 fade-up">
            <div
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/10 bg-white/5 backdrop-blur rounded-full"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4FF00] animate-pulse" />
              <span className="text-[11px] tracking-[0.22em] uppercase font-medium text-white/70">
                Works with every major store
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] mt-6"
            >
              Influencers <br className="hidden sm:block" />
              are <span className="text-[#D4FF00]">already</span> <br className="hidden sm:block" />
              your customers.
            </h1>

            <p
              data-testid="hero-subhead"
              className="mt-6 text-base md:text-lg text-[#A1A1AA] max-w-xl leading-relaxed"
            >
              InfluenceDetector automatically scans every order from your store — Shopify, WooCommerce, BigCommerce, Wix, Squarespace, or Magento — matches buyers to their social profiles, and surfaces the creators hiding in your customer list before your competitors reach them.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="hero-install-btn"
                onClick={() => {
                  trackCTA("signup_free", "hero");
                  document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="btn-lime rounded-sm h-14 px-7 text-sm hover:text-black"
              >
                Sign up Free
                <ArrowRight className="ml-2 w-4 h-4" strokeWidth={2.5} />
              </Button>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-[#A1A1AA]">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#D4FF00]" />
                No credit card · 7-day free trial
              </div>
              <div className="flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-[#D4FF00]" />
                2,400+ stores on the waitlist
              </div>
            </div>
          </div>

          {/* Right - product visual */}
          <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: "160ms" }}>
            <HeroMock />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroMock() {
  const rows = [
    { name: "Maya Okafor", handle: "@maya.okafor", followers: "184K", score: 92, spend: "$1,240" },
    { name: "Jordan Park", handle: "@jordancreates", followers: "63K", score: 87, spend: "$2,180" },
    { name: "Lena Fischer", handle: "@lenafisch", followers: "312K", score: 84, spend: "$640" },
    { name: "Tomás Ruiz", handle: "@tomaz.r", followers: "29K", score: 76, spend: "$980" },
    { name: "Priya Shah", handle: "@priyashah", followers: "510K", score: 95, spend: "$3,420" },
  ];
  return (
    <div className="relative">
      <div className="absolute -inset-10 bg-[#D4FF00]/10 blur-3xl rounded-full pointer-events-none" />
      <div
        data-testid="hero-mock"
        className="relative bg-[#0E0E10] border border-white/10 rounded-sm overflow-hidden shadow-2xl"
      >
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/5 bg-black/40">
          <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
          <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
          <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
          <span className="ml-3 text-[11px] font-mono-id text-white/40">
            dashboard.influencedetector.com
          </span>
        </div>

        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">
                Detected creators
              </div>
              <div className="font-display text-3xl mt-1">
                1,284 <span className="text-[#D4FF00]">+37</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">
                Reach unlocked
              </div>
              <div className="font-display text-3xl mt-1">42.8M</div>
            </div>
          </div>

          <div className="space-y-2.5">
            {rows.map((r) => (
              <div
                key={r.handle}
                className="flex items-center justify-between px-3 py-2.5 bg-white/[0.02] border border-white/5 rounded-sm hover:border-[#D4FF00]/40 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4FF00] to-fuchsia-500 flex items-center justify-center text-black text-xs font-bold flex-shrink-0">
                    {r.name.split(" ").map((n) => n[0]).join("")}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{r.name}</div>
                    <div className="text-[11px] text-white/40 font-mono-id truncate">
                      {r.handle}
                    </div>
                  </div>
                </div>
                <div className="hidden sm:block text-xs text-white/60 font-mono-id">
                  {r.followers}
                </div>
                <div className="text-xs text-white/60 font-mono-id hidden md:block">
                  {r.spend}
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-14 h-1 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#D4FF00]"
                      style={{ width: `${r.score}%` }}
                    />
                  </div>
                  <span className="text-[11px] font-mono-id text-[#D4FF00] w-6 text-right">
                    {r.score}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
